/**
 * Utility functions for parsing and handling links in text
 */

/**
 * Regular expression to match URLs in text
 * Matches http://, https://, www., and basic domain patterns
 */
const URL_REGEX = /(https?:\/\/[^\s]+|www\.[^\s]+|[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.(?:com|org|net|edu|gov|io|co|uk|de|fr|jp|cn|in|au|ca|br|ru|mx|za|sg|kr|tw|hk|it|es|nl|se|no|dk|fi|be|at|ch|ie|pl|cz|hu|ro|bg|hr|si|sk|lt|lv|ee|mt|cy|lu|is|li|ad|mc|sm|va|md|by|ua|kz|uz|kg|tj|tm|af|pk|bd|np|bt|lk|mv|mm|th|la|kh|vn|my|bn|id|ph|tl|pg|sb|vu|fj|ws|to|ki|nr|tv|fm|mh|pw|gu|mp|as|pr|vi|um|aq)[^\s]*)/gi;

/**
 * Parse text and extract URLs with their positions
 * @param {string} text - The text to parse
 * @returns {Array} Array of objects containing url, text, startIndex, endIndex
 */
export const extractUrls = (text) => {
  const urls = [];
  let match;
  
  // Reset regex lastIndex
  URL_REGEX.lastIndex = 0;
  
  while ((match = URL_REGEX.exec(text)) !== null) {
    urls.push({
      url: match[0],
      text: match[0],
      startIndex: match.index,
      endIndex: match.index + match[0].length
    });
  }
  
  return urls;
};

/**
 * Split text into segments with URLs and regular text
 * @param {string} text - The text to parse
 * @returns {Array} Array of segments with type 'text' or 'link'
 */
export const parseTextWithLinks = (text) => {
  if (!text || typeof text !== 'string') {
    return [{ type: 'text', content: text || '' }];
  }

  const urls = extractUrls(text);
  
  if (urls.length === 0) {
    return [{ type: 'text', content: text }];
  }
  
  const segments = [];
  let lastIndex = 0;
  
  urls.forEach((urlInfo) => {
    // Add text before the URL
    if (urlInfo.startIndex > lastIndex) {
      const textBefore = text.substring(lastIndex, urlInfo.startIndex);
      if (textBefore) {
        segments.push({ type: 'text', content: textBefore });
      }
    }
    
    // Add the URL
    segments.push({
      type: 'link',
      content: urlInfo.text,
      url: urlInfo.url.startsWith('http') ? urlInfo.url : `https://${urlInfo.url}`
    });
    
    lastIndex = urlInfo.endIndex;
  });
  
  // Add remaining text after the last URL
  if (lastIndex < text.length) {
    const textAfter = text.substring(lastIndex);
    if (textAfter) {
      segments.push({ type: 'text', content: textAfter });
    }
  }
  
  return segments;
};

/**
 * Format URL for display (remove protocol, truncate if too long)
 * @param {string} url - The URL to format
 * @param {number} maxLength - Maximum length for display
 * @returns {string} Formatted URL
 */
export const formatUrlForDisplay = (url, maxLength = 50) => {
  let displayUrl = url.replace(/^https?:\/\//, '').replace(/^www\./, '');
  
  if (displayUrl.length > maxLength) {
    displayUrl = displayUrl.substring(0, maxLength - 3) + '...';
  }
  
  return displayUrl;
};